import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Delete,
  ExpandMore,
  Add,
  AccountTree,
} from '@mui/icons-material';
import { Rule, SubRule, Gateway, ValidationResult } from '../../types/dynamicRouting';
import ConditionsEditor from './ConditionsEditor';
import GatewayDistributionEditor from './GatewayDistributionEditor';

interface RuleEditorProps {
  rule: Rule;
  gateways: Gateway[];
  onChange: (rule: Rule) => void;
  onRemove: () => void;
  canRemove: boolean;
  validationResult?: ValidationResult;
  rulePath: string;
  level: number;
  collapseSignal?: number;
}

const RuleEditor: React.FC<RuleEditorProps> = ({
  rule,
  gateways,
  onChange,
  onRemove,
  canRemove,
  validationResult,
  rulePath,
  level,
  collapseSignal,
}) => {
  const [expanded, setExpanded] = useState<string | false>('basic');

  // Collapse all local accordions when collapseSignal changes
  React.useEffect(() => {
    if (collapseSignal !== undefined) {
      setExpanded(false);
    }
  }, [collapseSignal]);

  const handleChange = (field: keyof Rule, value: any) => {
    onChange({
      ...rule,
      [field]: value,
    });
  };

  const handleSubRuleChange = (index: number, subRule: SubRule) => {
    const newSubRules = [...(rule.subRules || [])];
    newSubRules[index] = subRule;
    handleChange('subRules', newSubRules);
  };

  const handleAddSubRule = () => {
    const newSubRule: SubRule = {
      id: crypto.randomUUID(),
      name: `Sub Rule ${(rule.subRules || []).length + 1}`,
      priority: 100,
      conditions: {},
      gatewayDistribution: [
        {
          gatewayId: gateways.length > 0 ? gateways[0].id : '',
          percentage: 100,
        },
      ],
      enabled: true,
    };
    const subRules = rule.subRules || [];
    handleChange('subRules', [...subRules, newSubRule]);
  };

  const handleRemoveSubRule = (index: number) => {
    const newSubRules = (rule.subRules || []).filter((_, i) => i !== index);
    handleChange('subRules', newSubRules.length > 0 ? newSubRules : undefined);
  };

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const hasSubRules = rule.subRules && rule.subRules.length > 0;
  const indentLevel = level * 20;

  // Helper functions for readable summaries
  const getConditionsSummary = (): string => {
    if (!rule.conditions) return 'No conditions';

    const conditions: string[] = [];

    // String conditions
    Object.entries(rule.conditions).forEach(([field, condition]) => {
      if (typeof condition === 'object' && condition !== null && 'operator' in condition) {
        const stringCond = condition as any;
        const operator = stringCond.operator || 'equals';
        const value = Array.isArray(stringCond.value) ? stringCond.value.join(', ') : stringCond.value;

        if (value) {
          const operatorMap: Record<string, string> = {
            'equals': '=',
            'not_equals': '≠',
            'contains': 'contains',
            'not_contains': 'not contains',
            'starts_with': 'starts with',
            'ends_with': 'ends with',
            'in': 'in',
            'not_in': 'not in'
          };
          const operatorText = operatorMap[operator] || operator;

          conditions.push(`${field} ${operatorText} ${value}`);
        }
      } else if (typeof condition === 'number') {
        conditions.push(`${field} = ${condition}`);
      }
    });

    return conditions.length > 0 ? conditions.join(' AND ') : 'No conditions';
  };

  const getGatewayDistributionSummary = () => {
    if (!rule.gatewayDistribution || rule.gatewayDistribution.length === 0) {
      return 'No distribution';
    }

    return rule.gatewayDistribution
      .map(dist => {
        const gateway = gateways.find(g => g.id === dist.gatewayId);
        const gatewayName = gateway ? gateway.name : dist.gatewayId;
        return `${gatewayName} (${dist.percentage}%)`;
      })
      .join(', ');
  };

  const getSubRulesSummary = () => {
    if (!hasSubRules) return 'No sub-rules';
    return `${rule.subRules!.length} sub-rule${rule.subRules!.length === 1 ? '' : 's'}`;
  };

  return (
    <Box sx={{ ml: `${indentLevel}px`, border: level > 0 ? '1px solid #e0e0e0' : 'none', borderRadius: 1, p: level > 0 ? 2 : 0 }}>
      {/* Rule Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {level > 0 && <AccountTree color="action" />}
          {/* <Typography variant={level === 0 ? 'h6' : 'subtitle1'} color={level === 0 ? 'primary' : 'text.primary'}>
            {level === 0 ? 'Rule Configuration' : `Sub-Rule ${rule.name || 'Unnamed'}`}
          </Typography> */}
        </Box>

        {canRemove && (
          <IconButton color="error" onClick={onRemove} size="small">
            <Delete />
          </IconButton>
        )}
      </Box>

      {/* Basic Information */}
      <Accordion expanded={expanded === 'basic'} onChange={handleAccordionChange('basic')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            {expanded === 'basic' ? 'Basic Information' : `Rule: ${rule.name || 'Unnamed Rule'}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} >
              <TextField
                fullWidth
                label="Rule ID"
                value={rule.id || ''}
                placeholder="unique_rule_id"
                helperText="Auto-generated UUID for this rule"
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Rule Name"
                value={rule.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Rule Display Name"
                helperText="Human-readable name for this rule"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Conditions */}
      <Accordion expanded={expanded === 'conditions'} onChange={handleAccordionChange('conditions')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            {expanded === 'conditions' ? 'Conditions' : `Conditions: ${getConditionsSummary()}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ConditionsEditor
            conditions={rule.conditions}
            onChange={(conditions) => handleChange('conditions', conditions)}
            validationResult={validationResult}
            conditionsPath={`${rulePath}.conditions`}
          />
        </AccordionDetails>
      </Accordion>

      {/* Sub-Rules */}
      <Accordion expanded={expanded === 'subrules'} onChange={handleAccordionChange('subrules')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            {expanded === 'subrules' ? 'Sub-Rules' : `Sub-Rules: ${getSubRulesSummary()}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {/* Existing Sub-Rules */}
            {(rule.subRules || []).map((subRule, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <RuleEditor
                  rule={subRule}
                  gateways={gateways}
                  onChange={(updatedSubRule) => handleSubRuleChange(index, updatedSubRule)}
                  onRemove={() => handleRemoveSubRule(index)}
                  canRemove={true}
                  validationResult={validationResult}
                  rulePath={`${rulePath}.subRules[${index}]`}
                  level={level + 1}
                  collapseSignal={collapseSignal}
                />
              </Box>
            ))}

            {/* Add Sub-Rule Button */}
            <Box sx={{ textAlign: 'center', mt: 3, p: 3, border: '2px dashed #e0e0e0', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create a new sub-rule to handle specific conditions within this rule.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSubRule}
                size="large"
              >
                Add Sub-Rule
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Gateway Distribution - Evaluated after all sub-rules */}
      <Accordion expanded={expanded === 'distribution'} onChange={handleAccordionChange('distribution')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            {expanded === 'distribution' ? 'Gateway Distribution (Fallback)' : `Distribution: ${getGatewayDistributionSummary()}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            This distribution is used when none of the sub-rules above match the payment conditions.
          </Alert>
          <GatewayDistributionEditor
            distribution={rule.gatewayDistribution || []}
            gateways={gateways}
            onChange={(distribution) => handleChange('gatewayDistribution', distribution)}
            validationResult={validationResult}
            distributionPath={`${rulePath}.gatewayDistribution`}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default RuleEditor;
